// resources/js/src/components/community/components/post/PostFeed.tsx
import React, { useEffect, useRef, useMemo } from "react";
import type { CommunityPost } from "./interfaces/post.interface";
import { createPostStore, type FeedMode } from "../../../../store/postStore";
import FacebookPost from "./FacebookPost";

type Props = {
  mode?: FeedMode;
  /**
   * store جاهزة (اختياري) — لو متبعتة، PostFeed يستخدمها بدل ما يعمل
   * واحدة جديدة. ده مفيد في البروفايل عشان نشارك نفس الـ mine store
   * مع CreatePost (فالبوست الجديد يظهر فوراً).
   */
  useStoreOverride?: ReturnType<typeof createPostStore>;
  onEdit?: (post: CommunityPost) => void;
  onDelete?: (post: CommunityPost) => void;
};

const PostFeed: React.FC<Props> = ({
  mode = { type: "feed" },
  useStoreOverride,
  onEdit,
  onDelete,
}) => {
  // ==================== اختيار الـ store ====================
  const autoStore = useMemo(
    () => createPostStore(mode),
    [mode.type, mode.type === "user" ? mode.userId : null]
  );

  const useStore = useStoreOverride ?? autoStore;

  const {
    posts,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadFeed,
    loadMore,
  } = useStore();

  const loaderRef = useRef<HTMLDivElement>(null);

  // ==================== Initial load ====================
  useEffect(() => {
    if (posts.length === 0) {
      loadFeed();
    }
  }, [posts.length, loadFeed]);

  // ==================== Infinite scroll ====================
  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  // ==================== Loading ====================
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // ==================== Error ====================
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error}</p>
        <button
          onClick={() => loadFeed(true)}
          className="mt-3 px-4 py-2 rounded bg-primary-600 text-white"
        >
          Try Again
        </button>
      </div>
    );
  }

  // ==================== Empty ====================
  if (posts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {mode.type === "mine"
          ? "You haven't posted anything yet."
          : mode.type === "user"
          ? "No posts to show."
          : "No posts yet. Be the first to share something!"}
      </div>
    );
  }

  // ==================== Render ====================
  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <FacebookPost
          key={post.id}
          post={post}
          useStore={useStore}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}

      <div ref={loaderRef} className="h-4" />

      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      )}

      {!hasMore && posts.length > 0 && (
        <p className="text-center text-sm text-gray-400 py-4">No more posts</p>
      )}
    </div>
  );
};

export default PostFeed;